import * as THREE from "three";
import { groundAt } from "../terrain";
import { CloudShadows } from "../cloudShadows";
import { QUALITY_TIERS, startingTier, tierBand } from "../quality";
import { reducedVfx } from "../../ui/settings";

/**
 * The part of the sprite VFX module (art/vfx.js) the ambient meadow uses: raw spawns at the `ambient` priority class,
 * and a look at how full the pool is. Not in vfx.d.ts (those are the art session's public calls); read structurally.
 */
export interface AmbientFx {
  spawn(p: Record<string, unknown>): unknown;
  prio: number;
  cap: number;
  lod: number;
  particles: { length: number };
}

/**
 * The living meadow (Phase 2 hero moment 5): falling leaves, pollen in the sun, butterflies, on top of the cloud
 * shadows, wind bands and bird flock from Phase 1. Everything here is AMBIENT — the lowest priority class in the
 * particle budget (vfx.md F12): it is the first thing to go when the pool fills, it scales with the quality tier, and
 * reduced effects turns it off.
 */
export class Meadow {
  /** The level's sprite VFX, once its atlas has loaded (the level sets it; null in the hub and before load). */
  static vfx: AmbientFx | null = null;
  /** `PRIO.ambient` in art/vfx.js. */
  static readonly AmbientPrio = 0;
  /** The pool fill above which vfx.js drops ambient and trail spawns; the pollen fades out past it too. */
  static readonly Crowded = 0.85;

  static attach(vfx: unknown): void { Meadow.vfx = (vfx as AmbientFx | null) ?? null; }

  /**
   * The five-step band (0 ultra … 4 lowest) of the tier the device starts on, through `tierBand` rather than the raw
   * index, so `PollenByTier` stays right if the tier list changes (an eight-tier list once switched pollen off).
   */
  static tier(): number { try { return Meadow.band(startingTier()); } catch { return 1; } }
  static band(tierIndex: number): number { return tierBand(QUALITY_TIERS[tierIndex] ?? QUALITY_TIERS[0]).index; }

  /** True while the sprite pool has room for ambient spawns (always true when there is no pool to crowd). */
  static roomy(): boolean {
    const fx = Meadow.vfx;
    return !fx || fx.particles.length < fx.cap * Meadow.Crowded;
  }

  /** Pollen motes by tier; none on low/lowest or with reduced effects. */
  static readonly PollenByTier = [160, 120, 60, 0, 0];
  static pollenCount(tier: number, reduced: boolean): number {
    if (reduced) return 0;
    return Meadow.PollenByTier[Math.max(0, Math.min(Meadow.PollenByTier.length - 1, tier | 0))];
  }

  /**
   * Seconds between leaves from one tree: one every ~4 s on ultra, stretched by the tier's sprite LOD, off with
   * reduced effects. A gust shakes a small flurry loose on top (FoliageView).
   */
  static leafInterval(lod: number, reduced: boolean): number {
    if (reduced || lod <= 0) return Infinity;
    return 4 / Math.min(1, lod);
  }

  /** Spawns one falling leaf at `at` as an ambient sprite. Returns false when the budget refused it or there is no pool. */
  static leaf(at: THREE.Vector3, autumn: boolean, wind: readonly [number, number]): boolean {
    const fx = Meadow.vfx;
    if (!fx || !Meadow.roomy()) return false;
    const prev = fx.prio;
    fx.prio = Meadow.AmbientPrio;
    const r = Math.random;
    const colour = autumn ? LEAF_AUTUMN[Math.floor(r() * LEAF_AUTUMN.length)] : LEAF_GREEN[Math.floor(r() * LEAF_GREEN.length)];
    const drift = 0.25 + r() * 0.2;
    // Falls at ~0.4 u/s (gravity against heavy drag), carried down the wind, spinning — the spin on the 'debris'
    // card is the flutter. It settles on the ground (`floor`) and fades there.
    const p = fx.spawn({
      pos: _pos.copy(at), vel: _vel.set(wind[0] * drift + (r() - 0.5) * 0.15, -0.05, wind[1] * drift + (r() - 0.5) * 0.15),
      gravity: 0.7, drag: 1.6, life: 5 + r() * 2, size0: 0.06, size1: 0.05, color0: colour, additive: false,
      frame: "debris", rot: r() * 6.3, rotVel: (r() - 0.5) * 7, fadeIn: 0.05, fadeOut: 0.25, alpha: 0.95,
      floor: groundAt(at.x, at.z) + 0.01,
    });
    fx.prio = prev;
    return p !== null && p !== undefined && (p as { life?: number }).life !== undefined;
  }

  static reduced(): boolean { try { return reducedVfx(); } catch { return false; } }
}
const _pos = new THREE.Vector3(), _vel = new THREE.Vector3();
/** Leaf colours in the sprite shader's linear 0..1 RGB: oak greens going yellow, and the autumn oak's rusts. */
const LEAF_GREEN: [number, number, number][] = [[0.36, 0.46, 0.12], [0.52, 0.56, 0.16], [0.62, 0.55, 0.14]];
const LEAF_AUTUMN: [number, number, number][] = [[0.78, 0.38, 0.08], [0.86, 0.55, 0.12], [0.62, 0.22, 0.06]];

/**
 * Pollen and dust motes drifting in the sunlight (terrain F9): one `Points` draw, positioned entirely on the GPU in a
 * box that follows the point the camera looks at, wrapping at its edges (faded there, so the wrap never shows). They
 * drift down the wind and bob, twinkle, and dim under the cloud shadows — they are lit by the sun, so a cloud puts
 * them out. Additive and faint (0.3): a sparkle in the light, never a snowstorm. Zero CPU per frame beyond one
 * ray/plane intersection in `onBeforeRender`.
 */
export class PollenMotes {
  readonly points: THREE.Points;
  private readonly mat: THREE.ShaderMaterial;
  /** The box the motes fill round the camera's focus, in world units (x/z span, height). */
  static readonly Box = 14;
  static readonly Height = 2.6;
  /** Apparent size of a mote, in world units (a few pixels on a phone). */
  static readonly Size = 0.035;
  private fade = 1;
  private readonly ray = new THREE.Vector3();

  constructor(count: number, wind: readonly [number, number]) {
    const g = new THREE.BufferGeometry();
    const seed = new Float32Array(count * 4);
    for (let i = 0; i < count * 4; i++) seed[i] = Math.random();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(count * 3), 3));   // unused: placed in the shader
    g.setAttribute("aSeed", new THREE.BufferAttribute(seed, 4));
    this.mat = new THREE.ShaderMaterial({
      uniforms: {
        ...CloudShadows.uniforms,
        uCenter: { value: new THREE.Vector3() },
        uTime: { value: 0 },
        uPx: { value: 600 },
        uFade: { value: 1 },
        uWind: { value: new THREE.Vector2(wind[0], wind[1]) },
      },
      vertexShader: /* glsl */ `
        attribute vec4 aSeed;
        uniform vec3 uCenter; uniform float uTime; uniform float uPx; uniform vec2 uWind;
        varying float vA; varying vec3 vWP;
        void main() {
          const float B = ${PollenMotes.Box.toFixed(1)}, H = ${PollenMotes.Height.toFixed(1)};
          vec3 p = vec3(aSeed.x * B, aSeed.y * H, aSeed.z * B);
          // down the wind at 0.25 u/s, a slow rise, and a lazy loop of its own
          p.xz += uWind * uTime * 0.25 + vec2(sin(uTime * 0.7 + aSeed.w * 40.0), cos(uTime * 0.53 + aSeed.w * 23.0)) * 0.18;
          p.y += uTime * 0.04 + sin(uTime * 0.9 + aSeed.w * 17.0) * 0.1;
          // wrap into the box round the focus
          vec2 lo = uCenter.xz - B * 0.5;
          p.xz = lo + mod(p.xz - lo, B);
          p.y = mod(p.y, H);
          vec2 e = min(p.xz - lo, lo + B - p.xz) / (B * 0.18);
          float edge = clamp(min(e.x, e.y), 0.0, 1.0) * smoothstep(0.0, 0.4, p.y) * (1.0 - smoothstep(H - 0.5, H, p.y));
          p.y += uCenter.y + 0.15;
          vWP = p;
          vA = edge * (0.55 + 0.45 * sin(uTime * 2.3 + aSeed.w * 61.0));   // twinkle
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          gl_Position = projectionMatrix * mv;
          gl_PointSize = max(1.0, ${PollenMotes.Size.toFixed(3)} * projectionMatrix[1][1] * uPx / -mv.z);
        }`,
      fragmentShader: /* glsl */ `
        uniform float uFade;
        varying float vA; varying vec3 vWP;
        ${CloudShadows.glsl}
        void main() {
          vec2 c = gl_PointCoord - 0.5;
          float r = 1.0 - smoothstep(0.1, 0.5, length(c));
          // lit by the sun: under a cloud a mote nearly vanishes
          float sun = smoothstep(0.72, 1.0, cloudShade(vWP.xz).x);
          gl_FragColor = vec4(vec3(1.0, 0.9, 0.62) * 0.3 * r * vA * uFade * (0.25 + 0.75 * sun), 1.0);
        }`,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    this.points = new THREE.Points(g, this.mat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 2;
    this.points.onBeforeRender = (renderer, _scene, camera) => {
      // the focus: where the view ray meets the ground's rough level
      const u = this.mat.uniforms;
      camera.getWorldDirection(this.ray);
      const c = u.uCenter.value as THREE.Vector3;
      const t = Math.abs(this.ray.y) > 0.05 ? -camera.position.y / this.ray.y : 10;
      c.copy(camera.position).addScaledVector(this.ray, Math.max(0, t));
      c.y = groundAt(c.x, c.z);
      u.uPx.value = renderer.domElement.height * 0.5;
    };
  }

  update(dt: number, t: number): void {
    // ambient: fade out while the sprite pool is crowded, back in when it clears
    const want = Meadow.roomy() ? 1 : 0;
    this.fade += (want - this.fade) * Math.min(1, dt * 2);
    this.mat.uniforms.uFade.value = this.fade;
    this.mat.uniforms.uTime.value = t;
    this.points.visible = this.fade > 0.01;
  }

  dispose(): void { this.points.geometry.dispose(); this.mat.dispose(); }
}

/**
 * A few butterflies over the grass beside the road: two instanced wing cards each, beating fast, wandering from spot
 * to spot a hand's height above the tufts. They leave when a wave starts (rise and fade) and drift back when it is
 * calm. One draw for all of them.
 */
export class Butterflies {
  readonly mesh: THREE.InstancedMesh;
  private readonly n: number;
  private readonly pos: THREE.Vector3[] = [];
  private readonly target: THREE.Vector3[] = [];
  private readonly phase: Float32Array;
  private readonly heading: Float32Array;
  private away = 0;
  private t = 0;
  private readonly m = new THREE.Matrix4();
  private readonly w = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly e = new THREE.Euler(0, 0, 0, "YXZ");
  private readonly s = new THREE.Vector3(1, 1, 1);
  private readonly v = new THREE.Vector3();
  static readonly Colours = [0xf2d15a, 0xf4efe0, 0xe8893a, 0x86aee6, 0xf2d15a];
  /** Half a wingspan, in world units (a soldier is 0.33). */
  static readonly Wing = 0.035;

  constructor(count: number, private readonly pick: () => THREE.Vector3 | null) {
    this.n = count;
    const W = Butterflies.Wing;
    const wing = new THREE.BufferGeometry();
    // a rounded forewing and hindwing as three triangles, root on the body axis (x = 0), spreading to +x
    wing.setAttribute("position", new THREE.Float32BufferAttribute([
      0, 0, 0.004, W, 0, 0.02, W * 0.9, 0, -0.004,
      0, 0, 0.004, W * 0.9, 0, -0.004, W * 0.55, 0, -0.022,
      0, 0, 0.004, W * 0.55, 0, -0.022, 0, 0, -0.012,
    ], 3));
    wing.computeVertexNormals();
    const mat = new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0, side: THREE.DoubleSide });
    this.mesh = new THREE.InstancedMesh(wing, mat, count * 2);
    this.mesh.castShadow = false;
    this.mesh.frustumCulled = false;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    const col = new THREE.Color();
    for (let i = 0; i < count; i++) {
      col.set(Butterflies.Colours[i % Butterflies.Colours.length]);
      this.mesh.setColorAt(i * 2, col); this.mesh.setColorAt(i * 2 + 1, col);
      const p = pick() ?? new THREE.Vector3();
      this.pos.push(p.clone()); this.target.push(p.clone());
    }
    this.phase = Float32Array.from({ length: count }, () => Math.random() * 6.3);
    this.heading = new Float32Array(count);
  }

  update(dt: number, threatened: boolean): void {
    this.t += dt;
    this.away = Math.max(0, Math.min(1, this.away + (threatened ? dt : -dt * 0.4)));
    const hide = this.away >= 0.999;
    this.mesh.visible = !hide;
    if (hide) return;
    for (let i = 0; i < this.n; i++) {
      const p = this.pos[i], tg = this.target[i];
      this.v.copy(tg).sub(p); this.v.y = 0;
      const d = this.v.length();
      if (d < 0.08) { const nx = this.pick(); if (nx) tg.copy(nx); }
      else {
        // flutter toward the target on a wobbling line
        const ph = this.phase[i];
        const want = Math.atan2(this.v.x, this.v.z) + Math.sin(this.t * 3.1 + ph) * 0.9;
        let dh = want - this.heading[i];
        dh = Math.atan2(Math.sin(dh), Math.cos(dh));
        this.heading[i] += dh * Math.min(1, dt * 4);
        const sp = 0.35 * dt;
        p.x += Math.sin(this.heading[i]) * sp; p.z += Math.cos(this.heading[i]) * sp;
      }
      const ph = this.phase[i];
      const y = groundAt(p.x, p.z) + 0.16 + Math.sin(this.t * 4.3 + ph) * 0.04 + this.away * 1.5;
      const k = 1 - this.away;
      this.e.set(0, this.heading[i], 0); this.q.setFromEuler(this.e);
      this.v.set(p.x, y, p.z); this.s.setScalar(Math.max(0.001, k));
      this.m.compose(this.v, this.q, this.s);
      const flap = 0.2 + Math.abs(Math.sin(this.t * 17 + ph)) * 1.1;
      this.w.makeRotationZ(flap).premultiply(this.m);
      this.mesh.setMatrixAt(i * 2, this.w);
      this.w.makeRotationZ(-flap).premultiply(this.m).multiply(MIRROR);
      this.mesh.setMatrixAt(i * 2 + 1, this.w);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}
const MIRROR = new THREE.Matrix4().makeScale(-1, 1, 1);

/** The name of the tier a device is on, for notes and debugging. */
export function tierName(i: number): string { return QUALITY_TIERS[i]?.name ?? "?"; }
